// Pestaña "Logística" (formato nuevo). Nació como "Logística 2" (la revisión
// de Logística con los ojos de administración) y ahora es la única Logística;
// el componente y sus data-testid conservan el nombre logistica2.
//
// Tres subpestañas, como niveles, con "Anterior" y "Siguiente" al pie. Nada
// plegado: todo a la vista, en dos columnas en pantallas anchas.
// 1. Lugar y transporte: dirección, ruta desde la nave con el coste interno
//    del viaje (tiempo de la persona, combustible, desgaste por furgoneta y
//    total), transporte al cliente con "Cambiar" y la furgoneta.
// 2. Horario: horas de los servicios (las mismas que en el Resumen, con el
//    mismo control: HorasServicios) y horario y tramos del responsable (horas
//    redondeadas a 5 min, nombres llanos y el solape intercalado en la tabla).
//    Al guardar una hora se recargan Quién va (tramos) y las reglas.
// 3. Quién va y camareros: responsable del montaje, coste interno de
//    personal, personal del menú (solo los menús con camareros o extras; el
//    resto, en "Ver todos") y la solicitud al proveedor.
// Cada subpestaña lleva su indicador (resuelta o falta algo, con el motivo).
// Se abre el nivel pedido (`nivelPedido`, p. ej. desde Por hacer o
// ?nivel=), si no la primera con algo pendiente (o la 1), y se recuerda la
// última abierta de cada presupuesto en sessionStorage.
// `soloNivel`: pinta solo ese nivel, sin subpestañas ni "Anterior/Siguiente"
// (modal rápido "Dirección y transporte" de Por hacer).
// Multievento: arriba, el selector de jornadas (patrón de Material) con
// "Todas" (por defecto: resumen de las jornadas en tabla y lo común del
// evento). Con una jornada elegida, los tres niveles enseñan y editan lo de
// esa jornada: lugar, ruta y transporte; fecha y horas; y quién va (modo,
// persona y personal del menú propios de la jornada o heredados del evento).
// Lo de la jornada se guarda con `onGuardarJornada` (PUT de la jornada).
//
// No tiene lógica de negocio propia: datos, endpoints, cálculos
// (utils/quienVaEvento.js), manejadores de la ficha y la cola del
// autoguardado vienen de la ficha. El estado de Quién va vive en el hook
// useQuienVaEvento (una sola vez para toda la pestaña).
import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import useQuienVaEvento from '../../../hooks/useQuienVaEvento';
import usePuedeEditarHorarios from '../../../hooks/usePuedeEditarHorarios';
import useEsMovil from '../../../hooks/useEsMovil';
import { costeRutaInterno, direccionCorta, textoRutaInterna, urlMapa, urlIrAlEvento } from '../../../utils/logistica2';
import { HorarioTramos, PERSONA_DEL_EVENTO, PersonalMenu, ResponsableMontaje } from '../quienVa/BloquesQuienVa';
import SelectorJornadas from '../SelectorJornadas';
import PersonasJornada from '../PersonasJornada';
import {
  HorarioJornadaCampos, LugarJornada, ResumenJornadas, etiquetaJornada, presupuestoDeJornada,
} from './LogisticaJornada';
import LugarEvento2 from './LugarEvento2';
import CamarerosQuienVa from './CamarerosQuienVa';
import TransporteCliente from './TransporteCliente';
import CosteViaje, { totalViaje } from './CosteViaje';
import CostesPersonal from './CostesPersonal';
import HorasServicios, { serviciosSinHoraJornada } from '../HorasServicios';
import { Tarjeta, botonPrincipal, botonSecundario, claseSubtitulo, enlacePequeno } from './estilos';

// Orden de los niveles: primero el horario del evento y después quién va
export const SUBPESTANAS_L2 = [
  { clave: 'lugar', etiqueta: 'Lugar y transporte' },
  { clave: 'horario', etiqueta: 'Horario' },
  { clave: 'quien', etiqueta: 'Quién va y camareros' },
];

const claveMemoria = (id) => `logistica2_subpestana_${id}`;
const leerMemoria = (id) => {
  try {
    const v = window.sessionStorage.getItem(claveMemoria(id));
    return SUBPESTANAS_L2.some(s => s.clave === v) ? v : null;
  } catch (e) {
    return null;
  }
};
const guardarMemoria = (id, v) => {
  try { window.sessionStorage.setItem(claveMemoria(id), v); } catch (e) { /* sin almacenamiento */ }
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

const Logistica2 = ({
  presupuesto,
  // Lugar
  onCambiarLugar, onSeleccionarLugar, onCalcularRuta, calculandoRuta = false,
  // Transporte
  reglasComerciales = null, estadoReglas = null, accionEnCurso = null, avisoTransporte = null,
  onAnadirTransporte, onTransporteManual, onTransporteAutomatico, onMadridCentral, onCobrarTransporte,
  // Quién va
  hayOrdenExtra = false, personasOrdenExtra = 0, onGuardado, onAbrirSolicitud, onEstadoGuardado,
  autoguardado = null, onAbrirPersonal = null, numPersonalAsignado = 0, onPersonaGuardada, slotExtra = null,
  // Furgonetas (la tarjeta de siempre; sin ella no se pinta su tarjeta)
  slotFurgonetas = null,
  // Nivel pedido desde fuera (lugar | horario | quien) y aviso de que ya se abrió
  nivelPedido = null, onNivelAplicado = null,
  // Solo un nivel, sin subpestañas (modal rápido)
  soloNivel = null,
  // Multievento: guarda campos de una jornada (jornadaId, cambios) => Promise
  onGuardarJornada = null,
  // Multievento: tras cambiar las personas de una jornada (PersonasJornada)
  onPersonasJornada = null,
  // Horas de los servicios (Horario): guardar una línea de un evento de un día
  // (indice, cambios), "Proponer horas", recargar la ficha tras el endpoint de
  // horario del multievento y contador que sube tras guardar una hora (recarga
  // Quién va con las horas ya guardadas)
  onCambiarHorarioLinea = null, onProponerHoras = null, proponiendoHoras = false, onRecargarFicha = null,
  recargaHorarios = 0,
  // Antes del endpoint del horario del multievento: guardar lo pendiente de la ficha
  onAntesDeGuardarHorario = null,
}) => {
  // F1: sin el módulo de presupuestos, las horas (de la jornada y de los
  // servicios) se ven pero no se cambian
  const puedeEditarHorarios = usePuedeEditarHorarios();
  const p = presupuesto || {};
  const qv = useQuienVaEvento({
    presupuestoId: p.id, presupuesto: p, onGuardado, onEstadoGuardado, onPersonaGuardada, autoguardado,
    recarga: recargaHorarios,
  });
  const esNivel = (v) => SUBPESTANAS_L2.some(s => s.clave === v);
  const [sub, setSub] = useState(() => (esNivel(nivelPedido) ? nivelPedido : leerMemoria(p.id)));
  const [lugarEditando, setLugarEditando] = useState(false);
  const [transporteAbierto, setTransporteAbierto] = useState(false);

  const { raiz, jornadas, multi, modo, personaId, personaNombre, datos, cargando, sinCostePersona, calculo } = qv;
  const nombre = modo === 'propio' ? personaNombre : '';
  const t = estadoReglas?.transporte || null;
  const municipio = t?.municipio || null;
  const corta = direccionCorta(p.direccion_exacta || p.lugar_evento, municipio);
  const mapa = urlMapa(p);
  const faltaLugar = !p.lugar_evento;
  const faltaRuta = !!p.lugar_evento && !p.distancia_km;
  const verEditorLugar = lugarEditando || faltaLugar || faltaRuta;
  // Estado de la solicitud al proveedor (la orden única; en multievento
  // antiguo sin ella, la de la primera jornada que la tenga)
  const ordenExtra = p.orden_personal_extra
    || (p.jornadas || []).map(j => j && j.orden_personal_extra).find(Boolean) || null;
  const estadoOrdenExtra = ordenExtra ? (ordenExtra.estado || 'borrador') : null;
  const rutaBase = costeRutaInterno({ presupuesto: p, reglas: reglasComerciales, transporte: t, sinCoste: !!sinCostePersona && modo === 'propio' });

  // ----- Multievento: jornada elegida (null = "Todas") -----
  const multiP = !!p.es_multi_evento && Array.isArray(p.jornadas) && p.jornadas.length > 0;
  const [jornadaSel, setJornadaSel] = useState(null);
  const indiceJornada = multiP && jornadaSel ? p.jornadas.findIndex(j => j.id === jornadaSel) : -1;
  const jornadaP = indiceJornada >= 0 ? p.jornadas[indiceJornada] : null;
  const transporteJornadas = t?.jornadas || [];
  const tJ = jornadaP ? (transporteJornadas.find(x => x.id === jornadaP.id) || null) : null;
  const xJ = jornadaP ? ((qv.jornadas || []).find(x => x.j.id === jornadaP.id) || null) : null;
  const etiquetaJ = jornadaP ? `jornada ${indiceJornada + 1}` : '';
  // Total del viaje con el combustible al precio del día (el del desglose)
  const ruta = rutaBase ? { ...rutaBase, total: totalViaje(rutaBase, t) } : null;

  // ----- Qué falta en cada subpestaña (motivo; null si está resuelta) -----
  const motivos = {};
  {
    const m = [];
    if (faltaLugar) m.push('Falta la dirección del evento');
    else if (faltaRuta) m.push('Falta la ruta desde la nave');
    const marcaNoCobrar = p.transporte && typeof p.transporte === 'object' && p.transporte.cobrar === false;
    if (t && !t.linea && !marcaNoCobrar) m.push('Falta decidir si se cobra transporte');
    // Multievento: jornadas con dirección propia sin ruta
    const sinRutaJ = transporteJornadas.filter(x => !x.hereda_direccion && !(Number(x.distancia_km) > 0)).length;
    if (sinRutaJ > 0) m.push(`Falta la ruta de ${plural(sinRutaJ, 'jornada', 'jornadas')}`);
    motivos.lugar = m.join(' · ') || null;
  }
  if (datos) {
    const personasMenu = multi
      ? Math.max(0, ...jornadas.map(x => x.est.calculo?.personalMenu?.personas || 0))
      : (calculo?.personalMenu?.personas || 0);
    motivos.quien = [
      !modo ? 'Falta decidir quién va' : null,
      modo === 'propio' && !personaId ? 'Falta elegir la persona de Duetto' : null,
      modo === 'extra' && !hayOrdenExtra ? 'Falta pedir el extra al proveedor' : null,
      personasMenu > 0 && !hayOrdenExtra && modo !== 'extra'
        ? `Falta pedir ${plural(personasMenu, 'persona', 'personas')} de fuera al proveedor` : null,
    ].filter(Boolean).join(' · ') || null;
    const solapes = (multi ? jornadas.flatMap(x => x.est.solapes || []) : (raiz?.solapes || [])).filter(s => !s.decision);
    const h = estadoReglas?.horarios;
    // Multievento: también los días sin hora aunque la cabecera la tenga
    const sinHora = h && h.aplica ? (Number(h.sin_hora) || 0) + (h.sin_hora_jornadas || []).length : 0;
    motivos.horario = [
      solapes.length > 0 ? (solapes.length === 1 ? 'Solape de montaje sin decidir' : `${solapes.length} solapes de montaje sin decidir`) : null,
      sinHora > 0 ? `Falta la hora de ${plural(sinHora, 'servicio', 'servicios')}` : null,
      raiz && !raiz.horario && !multi ? 'Falta la hora del evento' : null,
    ].filter(Boolean).join(' · ') || null;
  }
  const listo = !!datos || (!cargando && !datos);
  const porDefecto = (SUBPESTANAS_L2.find(s => motivos[s.clave]) || SUBPESTANAS_L2[0]).clave;
  useEffect(() => {
    if (sub === null && listo && estadoReglas) setSub(porDefecto);
  }, [sub, listo, estadoReglas, porDefecto]);
  // Nivel pedido desde fuera (Por hacer, cabecera, enlace con ?nivel=)
  useEffect(() => {
    if (soloNivel || !SUBPESTANAS_L2.some(s => s.clave === nivelPedido)) return;
    setSub(nivelPedido);
    guardarMemoria(p.id, nivelPedido);
    if (onNivelAplicado) onNivelAplicado();
  }, [soloNivel, nivelPedido, p.id, onNivelAplicado]);
  const activa = (esNivel(soloNivel) && soloNivel) || sub || 'lugar';
  const indice = SUBPESTANAS_L2.findIndex(s => s.clave === activa);
  // Móvil (< 768 px): la subpestaña activa se trae a la vista dentro de la fila
  // de subpestañas (se desplaza en horizontal)
  const esMovil = useEsMovil();
  const filaSubpestanasRef = useRef(null);
  useEffect(() => {
    if (!esMovil || !filaSubpestanasRef.current) return;
    const boton = filaSubpestanasRef.current.querySelector('[aria-selected="true"]');
    if (boton && typeof boton.scrollIntoView === 'function') {
      boton.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
  }, [esMovil, activa]);
  const abrir = (clave) => {
    setSub(clave);
    guardarMemoria(p.id, clave);
    if (autoguardado && typeof autoguardado.guardarYa === 'function') autoguardado.guardarYa();
  };

  const indicador = (clave) => {
    if (clave !== 'lugar' && !datos) return null;
    const motivo = motivos[clave];
    return motivo ? (
      <span className="ml-1.5 w-4 h-4 bg-orange-500 text-white text-[10px] rounded-full inline-flex items-center justify-center font-bold"
        title={motivo} aria-label={motivo} data-testid={`l2-sub-aviso-${clave}`}>!</span>
    ) : (
      <span className="ml-1.5 w-4 h-4 bg-duetto-primary text-white rounded-full inline-flex items-center justify-center"
        title="Resuelto" aria-label="Resuelto" data-testid={`l2-sub-ok-${clave}`}>
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    );
  };

  // Estado para los bloques compartidos con la pestaña Logística
  const destino = corta.destino;
  const v = qv.datos && qv.raiz ? {
    ...qv,
    elegirModo: qv.setModo,
    presupuesto: p,
    // La solicitud al proveedor va en su propia tarjeta (al lado): sin duplicarla
    onAbrirSolicitud: null,
    hayOrdenExtra, personasOrdenExtra, slotExtra, slotPropio: null, onAbrirPersonal, numPersonalAsignado,
  } : null;
  // Multievento con una jornada elegida: el mismo estado visto como esa jornada
  // (su quién va, su horario y su personal; tramos y recogida son del evento)
  const externosDe = (est) => {
    const total = Number(est?.calculo?.personasExternas) || 0;
    const camareros = (est?.filas || []).reduce(
      (acc, f) => acc + (f?.cubre_responsable ? 0 : Math.max(0, parseInt(f?.camareros, 10) || 0)), 0);
    return { total, camareros: Math.min(camareros, total), extras: Math.max(0, total - camareros) };
  };
  const pJ = jornadaP ? presupuestoDeJornada(p, jornadaP, tJ, xJ?.modo) : p;
  const datosJ = xJ && qv.datos
    ? { ...qv.datos, ...((qv.datos.jornadas || []).find(d => d.id === xJ.j.id) || {}) } : null;
  const nombreJ = xJ && xJ.modo === 'propio' ? xJ.personaNombre : '';
  const vJ = v && xJ ? {
    ...v,
    multi: false,
    jornadas: [],
    raiz: xJ.est,
    calculo: xJ.est.calculo,
    comparativa: xJ.est.comparativa,
    datos: datosJ,
    tramos: xJ.est.tramos,
    modo: xJ.modo,
    elegirModo: (m) => qv.setModoJornada(xJ.j.id, m),
    setModo: (m) => qv.setModoJornada(xJ.j.id, m),
    personaId: xJ.personaId,
    personaNombre: xJ.personaNombre,
    sinCostePersona: xJ.sinCoste,
    guardarPersona: (id) => (id === PERSONA_DEL_EVENTO
      ? qv.guardarPersonaJornada(xJ.j.id, null, true)
      : qv.guardarPersonaJornada(xJ.j.id, id)),
    menuEditado: xJ.menuEditado,
    cambiarMenu: (clave, campo, texto) => qv.cambiarMenuJornada(xJ.j.id, clave, campo, texto),
    cambiarCubre: (clave, valor) => qv.cambiarCubreJornada(xJ.j.id, clave, valor),
    restablecerMenu: (clave) => qv.restablecerMenuJornada(xJ.j.id, clave),
    // Tandas de camareros y "Volver a lo propuesto" de esta jornada
    cambiarTandas: (clave, tandas) => qv.cambiarTandasJornada(xJ.j.id, clave, tandas),
    volverAPropuesto: (clave, defecto) => qv.volverAPropuestoJornada(xJ.j.id, clave, defecto),
    // Líneas con personal propio de la jornada ("Usar lo del evento")
    menuPropioJornada: (qv.porJornada?.[xJ.j.id] || {}).personal_menu_editado || {},
    ajustarExtremo: (tramo, hora) => qv.ajustarExtremo(tramo, hora, xJ.est.horario),
    externosCalculo: externosDe(xJ.est),
    presupuesto: pJ,
    ambitoJornada: {
      etiqueta: etiquetaJ,
      modoPropio: xJ.propio.modo,
      personaPropia: xJ.propio.persona,
      modoEvento: qv.modo,
      personaEventoNombre: qv.personaNombre,
      onHeredarModo: () => qv.setModoJornada(xJ.j.id, null),
    },
  } : null;
  const qvJ = xJ ? {
    ...qv,
    raiz: xJ.est,
    calculo: xJ.est.calculo,
    comparativa: xJ.est.comparativa,
    modo: xJ.modo,
    sinCostePersona: xJ.sinCoste,
    multi: false,
    jornadas: [],
    externosCalculo: externosDe(xJ.est),
  } : null;

  const cargandoQuienVa = cargando && !datos
    ? <p className="text-sm text-[#5B6B64]">Cargando quién va al evento...</p>
    : <p className="text-sm text-[#8A1C12]" data-testid="l2-quien-va-error">{qv.error}</p>;

  // ----- Contenido de cada subpestaña -----
  // Con la furgoneta al lado (2/3 + 1/3) en la pestaña; en una columna sin ella
  const conFurgoneta = !!slotFurgonetas;
  const renderLugarJornada = () => (
    <Tarjeta titulo={`Lugar de la ${etiquetaJ}`} testid="l2-tarjeta-lugar-jornada">
      <LugarJornada
        presupuesto={p}
        jornada={jornadaP}
        indice={indiceJornada}
        transporteJornada={tJ}
        reglasComerciales={reglasComerciales}
        modo={xJ ? xJ.modo : modo}
        sinCoste={xJ ? xJ.sinCoste : sinCostePersona}
        onGuardarJornada={onGuardarJornada}
      />
    </Tarjeta>
  );
  const renderLugar = () => (
    <div className={conFurgoneta ? 'grid grid-cols-1 xl:grid-cols-3 gap-6 items-start' : 'grid grid-cols-1 gap-6'}>
      <div className={`${conFurgoneta ? 'xl:col-span-2 ' : ''}min-w-0 flex flex-col gap-6`}>
        {jornadaP ? renderLugarJornada() : (
        <Tarjeta titulo="Lugar del evento" testid="l2-tarjeta-lugar">
          <div className="flex flex-col gap-3">
            {!faltaLugar && (
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm text-[#16221D]" data-testid="l2-lugar-texto">
                  <strong>{corta.linea || p.lugar_evento}</strong>
                  {t?.madrid_central_dentro === true && (
                    <span className="ml-2 inline-flex items-center rounded-full border border-[#F2C4BF] bg-[#FDECEA] px-2 py-0.5 text-xs md:text-[11px] font-semibold text-[#8A1C12] align-middle"
                      title="El lugar está dentro de la Zona de Bajas Emisiones Distrito Centro" data-testid="l2-lugar-madrid-central">
                      Dentro de Madrid Central
                    </span>
                  )}
                  {mapa && (
                    <>
                      {' '}
                      <a href={mapa} target="_blank" rel="noopener noreferrer" className={`ml-1 ${enlacePequeno}`} data-testid="l2-lugar-mapa">Ver en mapa</a>
                    </>
                  )}
                </p>
                {!verEditorLugar && (
                  <button type="button" onClick={() => setLugarEditando(true)} className={enlacePequeno} data-testid="l2-lugar-cambiar">Cambiar</button>
                )}
              </div>
            )}
            {!faltaLugar && urlIrAlEvento(p) && (
              <a href={urlIrAlEvento(p)} target="_blank" rel="noopener noreferrer"
                className={`self-start ${botonSecundario}`} data-testid="l2-lugar-ir-al-evento">
                Ir al evento
              </a>
            )}
            {ruta && (
              <p className="text-sm text-[#5B6B64]" data-testid="l2-ruta">
                Ruta desde la nave: {textoRutaInterna(ruta, { corto: true })}
              </p>
            )}
            {ruta && <CosteViaje ruta={ruta} transporte={t} />}
            {verEditorLugar && (
              <LugarEvento2
                presupuesto={p}
                onCambiarLugar={onCambiarLugar}
                onSeleccionarLugar={onSeleccionarLugar}
                onCalcularRuta={onCalcularRuta}
                calculandoRuta={calculandoRuta}
                onCerrar={lugarEditando && !faltaLugar && !faltaRuta ? () => setLugarEditando(false) : null}
              />
            )}
            <div className="border-t border-[#EEF1EF] pt-3 flex flex-col gap-2" data-testid="l2-tarjeta-transporte">
              <h3 className={claseSubtitulo}>Transporte al cliente</h3>
              <TransporteCliente
                presupuesto={p}
                estadoReglas={estadoReglas}
                accionEnCurso={accionEnCurso}
                avisoTransporte={avisoTransporte}
                abierto={transporteAbierto}
                onAbrir={setTransporteAbierto}
                onAnadirTransporte={onAnadirTransporte}
                onTransporteManual={onTransporteManual}
                onTransporteAutomatico={onTransporteAutomatico}
                onMadridCentral={onMadridCentral}
                onCobrarTransporte={onCobrarTransporte}
              />
            </div>
          </div>
        </Tarjeta>
        )}
      </div>
      {conFurgoneta && (
        <div className="min-w-0">
          <Tarjeta titulo="Furgoneta" testid="l2-tarjeta-furgonetas">{slotFurgonetas}</Tarjeta>
        </div>
      )}
    </div>
  );

  const renderQuienJornada = () => (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
      <Tarjeta titulo={`Quién va en la ${etiquetaJ}`} testid="l2-tarjeta-quien-va">
        <ResponsableMontaje v={vJ} />
      </Tarjeta>
      <Tarjeta titulo={`Coste interno de personal de la ${etiquetaJ}`} testid="l2-tarjeta-costes-personal">
        <CostesPersonal qv={qvJ} auto={!!autoguardado} jornadaEtiqueta={etiquetaJ} presupuesto={p} jornada={jornadaP} />
      </Tarjeta>
      <Tarjeta titulo={`Camareros y extras de la ${etiquetaJ}`} testid="l2-tarjeta-personal-menu">
        <PersonalMenu v={vJ} variante="l2" />
      </Tarjeta>
      <Tarjeta titulo="Solicitud al proveedor" testid="l2-tarjeta-camareros">
        <CamarerosQuienVa
          qv={qvJ}
          nombre={nombreJ}
          hayOrdenExtra={hayOrdenExtra}
          personasOrdenExtra={personasOrdenExtra}
          estadoOrdenExtra={estadoOrdenExtra}
          onAbrirSolicitud={onAbrirSolicitud}
        />
        <p className="mt-2 text-xs text-[#5B6B64]">
          La solicitud es una sola para todo el multievento, con un bloque por jornada.
        </p>
      </Tarjeta>
    </div>
  );
  const renderQuien = () => (vJ ? renderQuienJornada() : v ? (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
      <Tarjeta titulo="Quién va al evento" testid="l2-tarjeta-quien-va">
        <ResponsableMontaje v={v} />
      </Tarjeta>
      <Tarjeta titulo="Coste interno de personal" testid="l2-tarjeta-costes-personal">
        <CostesPersonal qv={qv} auto={!!autoguardado} presupuesto={p} />
      </Tarjeta>
      <Tarjeta titulo="Camareros y extras" testid="l2-tarjeta-personal-menu">
        <PersonalMenu v={v} variante="l2" />
      </Tarjeta>
      <Tarjeta titulo="Solicitud al proveedor" testid="l2-tarjeta-camareros">
        <CamarerosQuienVa
          qv={qv}
          nombre={nombre}
          hayOrdenExtra={hayOrdenExtra}
          personasOrdenExtra={personasOrdenExtra}
          estadoOrdenExtra={estadoOrdenExtra}
          onAbrirSolicitud={onAbrirSolicitud}
        />
      </Tarjeta>
    </div>
  ) : <Tarjeta titulo="Quién va al evento" testid="l2-tarjeta-quien-va">{cargandoQuienVa}</Tarjeta>);

  // Tarjeta "Horas de los servicios": la del evento (un día o la cabecera del
  // multievento con "Todas") o la de la jornada elegida
  const renderHorasServicios = () => (
    <Tarjeta titulo={jornadaP ? `Horas de los servicios de la ${etiquetaJ}` : 'Horas de los servicios'} testid="l2-tarjeta-horas-servicios">
      <HorasServicios
        presupuesto={p}
        estadoReglas={estadoReglas}
        jornada={jornadaP}
        onCambiarLinea={onCambiarHorarioLinea}
        onGuardarJornada={onGuardarJornada}
        onActualizado={onRecargarFicha}
        onProponerHoras={onProponerHoras}
        proponiendo={proponiendoHoras}
        onAntesDeGuardar={onAntesDeGuardarHorario}
      />
    </Tarjeta>
  );

  const renderHorario = () => {
    const h = estadoReglas?.horarios;
    // Servicios sin hora en lo que se ve: la jornada elegida o el evento
    const faltan = jornadaP
      ? (h ? serviciosSinHoraJornada(p, jornadaP, estadoReglas).map(s => s.nombre) : [])
      : (h && h.aplica ? [
        ...(Number(h.sin_hora) > 0 ? (h.nombres_sin_hora || []) : []),
        // Multievento: días sin hora aunque la cabecera la tenga
        ...(h.sin_hora_jornadas || []).map(x => `${x.nombre} (día ${x.numero})`),
      ] : []);
    const sinHora = jornadaP ? faltan.length
      : (h && h.aplica ? (Number(h.sin_hora) || 0) + (h.sin_hora_jornadas || []).length : 0);
    const aviso = sinHora > 0 ? (
      <p className="rounded-[10px] border px-3.5 py-2.5 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="l2-horario-sin-hora">
        Falta la hora de {plural(sinHora, 'servicio', 'servicios')}{faltan.length > 0 ? ` (${faltan.join(', ')})` : ''}: se pone en Horas de los servicios, aquí abajo, o en el Resumen.
      </p>
    ) : null;
    if (jornadaP) {
      return (
        <div className="flex flex-col gap-6">
          {aviso}
          <Tarjeta titulo={`Horario de la ${etiquetaJ}`} testid="l2-tarjeta-horario-jornada">
            <HorarioJornadaCampos jornada={jornadaP} indice={indiceJornada}
              onGuardarJornada={puedeEditarHorarios ? onGuardarJornada : null} />
            {onPersonasJornada && (
              <div className="mt-4 pt-4 border-t border-[#EEF1EF]">
                <PersonasJornada presupuestoId={p.id} jornada={jornadaP} onActualizado={onPersonasJornada} />
              </div>
            )}
          </Tarjeta>
          {renderHorasServicios()}
          <Tarjeta titulo={nombreJ ? `Horario de ${nombreJ} y tramos` : 'Horario del responsable y tramos'} testid="l2-tarjeta-horario">
            {vJ
              ? <HorarioTramos v={vJ} variante="l2" nombre={nombreJ} destino={direccionCorta(pJ.direccion_exacta || pJ.lugar_evento, tJ?.municipio).destino} />
              : cargandoQuienVa}
          </Tarjeta>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-6">
        {aviso}
        {renderHorasServicios()}
        <Tarjeta titulo={nombre ? `Horario de ${nombre} y tramos` : 'Horario del responsable y tramos'} testid="l2-tarjeta-horario">
          {v ? <HorarioTramos v={v} variante="l2" nombre={nombre} destino={destino} /> : cargandoQuienVa}
        </Tarjeta>
      </div>
    );
  };

  const selectorJornadas = multiP ? (
    <div className="flex flex-col gap-3" data-testid="l2-multievento">
      <SelectorJornadas
        jornadas={p.jornadas}
        jornadaId={jornadaP ? jornadaP.id : null}
        onChange={setJornadaSel}
        etiqueta={etiquetaJornada}
        conTodas
        testid="l2-jornadas"
        prefijoTestid="l2-jornada"
      />
      {!jornadaP && (
        <ResumenJornadas presupuesto={p} jornadasTransporte={transporteJornadas} onElegir={setJornadaSel} />
      )}
    </div>
  ) : null;

  const panel = (
    <>
      {activa === 'lugar' && renderLugar()}
      {activa === 'quien' && renderQuien()}
      {activa === 'horario' && renderHorario()}
    </>
  );
  if (esNivel(soloNivel)) {
    return (
      <div className="flex flex-col gap-5" data-testid="logistica2" data-solo-nivel={soloNivel}>
        {selectorJornadas}
        {panel}
      </div>
    );
  }

  const anterior = indice > 0 ? SUBPESTANAS_L2[indice - 1] : null;
  const siguiente = indice < SUBPESTANAS_L2.length - 1 ? SUBPESTANAS_L2[indice + 1] : null;

  return (
    <div className="flex flex-col gap-5" data-testid="logistica2">
      {selectorJornadas}
      <div ref={filaSubpestanasRef} className="flex gap-4 sm:gap-6 overflow-x-auto border-b border-[#E1E6E3]" role="tablist" aria-label="Logística"
        data-testid="l2-subpestanas">
        {SUBPESTANAS_L2.map((s, i) => {
          const sel = s.clave === activa;
          return (
            <button
              key={s.clave}
              type="button"
              role="tab"
              aria-selected={sel}
              onClick={() => abrir(s.clave)}
              className={`py-2 px-0.5 min-h-[44px] md:min-h-0 text-sm md:text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center ${
                sel ? 'text-[#11704F] border-duetto-primary' : 'text-[#5B6B64] border-transparent hover:text-[#16221D]'}`}
              data-testid={`l2-sub-${s.clave}`}
            >
              {i + 1}. {s.etiqueta}
              {indicador(s.clave)}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" data-testid={`l2-panel-${activa}`}>
        {panel}
      </div>

      <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3">
        {anterior ? (
          <button type="button" onClick={() => abrir(anterior.clave)} className={botonSecundario} data-testid="l2-anterior">
            Anterior: {anterior.etiqueta}
          </button>
        ) : <span />}
        {siguiente && (
          <button type="button" onClick={() => abrir(siguiente.clave)} className={botonPrincipal} data-testid="l2-siguiente">
            Siguiente: {siguiente.etiqueta}
          </button>
        )}
      </div>
    </div>
  );
};

export default Logistica2;
